import React from "react";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { useOptionalCounts } from "../../components/Layout/counts";
import { Promotion, PromotionDetail, PromotionsResponse, listPromotions } from "../../common/requests/promotions/endpoints";
import { Banner, Column, DataTable, KpiCard, KpiStrip, PageShell, useResource, useUrlParam } from "../../ui";
import PromotionDetailDialog from "./PromotionDetailDialog";
import PromotionDialog from "./PromotionDialog";
import StateBadge from "./StateBadge";
import { costLabel, datesLabel, howLabel, money, noteLabel, offerLabel, typeLabel, usedLabel } from "./promoText";

type TabKey = "all" | "live" | "scheduled" | "draft" | "paused" | "ended";

const TABS: { value: TabKey; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "scheduled", label: "Scheduled" },
  { value: "draft", label: "Drafts" },
  { value: "paused", label: "Paused" },
  { value: "ended", label: "Ended" },
];

const STATE_ORDER = { live: 0, paused: 1, scheduled: 2, draft: 3, ended: 4 } as const;

const COLUMNS: Column<Promotion>[] = [
  {
    key: "name",
    header: "Promotion",
    card: "title",
    minWidth: 200,
    sortValue: (p) => p.name.toLowerCase(),
    render: (p) => (
      <Box sx={{ minWidth: 0 }}>
        <Box component="b" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
          {p.name}
        </Box>
        <Box sx={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>{noteLabel(p)}</Box>
      </Box>
    ),
  },
  { key: "type", header: "Type", card: "subtitle", hideBelow: 1100, render: (p) => typeLabel(p) },
  { key: "offer", header: "Offer", render: (p) => offerLabel(p) },
  { key: "how", header: "How buyers get it", render: (p) => howLabel(p) },
  { key: "dates", header: "Dates", hideBelow: 960, sortValue: (p) => p.starts_at ?? p.created_at ?? "", render: (p) => datesLabel(p.starts_at, p.expires_at) },
  { key: "used", header: "Used", align: "right", sortValue: (p) => p.stats.uses, render: (p) => usedLabel(p) },
  { key: "cost", header: "Cost / budget", align: "right", sortValue: (p) => p.stats.cost, render: (p) => costLabel(p) },
  { key: "status", header: "Status", card: "aside", sortValue: (p) => STATE_ORDER[p.state], render: (p) => <StateBadge state={p.state} legacy={p.legacy} /> },
];

/** Marketing -> Promotions: every voucher and free-delivery promotion, its
 * numbers from paid orders, and the create / detail dialogs. The open
 * promotion lives in ?promotion=<id> so it can be linked to. */
export default function Promotions() {
  const { idToken } = useAuth();
  const counts = useOptionalCounts();
  const list = useResource<PromotionsResponse>(async () => (await listPromotions(idToken!)).data, [idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load promotions.",
  });
  const [tabParam, setTab] = useUrlParam("tab");
  const tab: TabKey = (TABS.find((t) => t.value === tabParam)?.value ?? "all") as TabKey;
  const [openId, setOpenId] = useUrlParam("promotion");
  const [editing, setEditing] = React.useState<{ promotion: Promotion | null } | null>(null);
  // Bumped after a save so an open detail dialog loads the new settings.
  const [version, setVersion] = React.useState(0);

  const data = list.data;
  const rows = React.useMemo(() => (data?.promotions ?? []).filter((p) => tab === "all" || p.state === tab), [data, tab]);
  const summary = data?.summary;

  const refresh = () => {
    list.reload();
    counts?.reload();
  };

  const onSaved = (saved: PromotionDetail) => {
    setEditing(null);
    setVersion((v) => v + 1);
    refresh();
    setOpenId(saved.id, !openId);
  };

  const tabs = TABS.filter((t) => t.value !== "paused" || (data?.counts.paused ?? 0) > 0).map((t) => ({
    value: t.value,
    label: t.label,
    count: data ? data.counts[t.value] : null,
  }));

  return (
    <PageShell
      title="Promotions"
      subtitle="Vouchers and free delivery. Every number here is from paid orders."
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ promotion: null })} disabled={!data?.available}>
          New promotion
        </Button>
      }
      tabs={{ value: tab, onChange: (v) => setTab(v === "all" ? null : v), items: tabs }}
    >
      {data && !data.available && (
        <Banner tone="warning" title="Not installed yet.">
          Promotions need marketplace migration 021 on this database. Old Stripe codes are shown below; new promotions can be made once it runs.
        </Banner>
      )}
      {summary && (
        <KpiStrip>
          <KpiCard title="Live now" value={String(summary.live_now)} />
          <KpiCard title="Vouchers used, last 30 days" value={String(summary.uses_30d)} />
          <KpiCard title="Cost, last 30 days" value={money(summary.cost_30d)} />
          <KpiCard
            title="All time"
            value={`${summary.all_time.uses.toLocaleString("en-US")} uses`}
            sub={`${money(summary.all_time.cost)} cost · ${money(summary.all_time.gmv)} orders`}
          />
        </KpiStrip>
      )}
      <DataTable
        aria-label="Promotions"
        rows={rows}
        columns={COLUMNS}
        getRowId={(p) => p.id}
        loading={list.status === "loading" || list.status === "idle"}
        error={list.error}
        onRetry={list.reload}
        onRowClick={(p) => setOpenId(p.id, true)}
        selectedId={openId}
        empty={tab === "all" ? "No promotions yet. Make the first one with New promotion." : "Nothing here."}
      />
      <PromotionDetailDialog
        id={openId}
        version={version}
        onClose={() => setOpenId(null)}
        onEdit={(p) => setEditing({ promotion: p })}
        onChanged={(nextId) => {
          refresh();
          if (nextId) setOpenId(nextId);
        }}
      />
      <PromotionDialog open={!!editing} promotion={editing?.promotion} onClose={() => setEditing(null)} onSaved={onSaved} />
    </PageShell>
  );
}
